import { Description } from "@mui/icons-material";
import { Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
	InputLabel, MenuItem, Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField,
	Tooltip } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { FinancialService } from "../../../services/FinancialService";
import { FinancialBillDTO, FinancialReviewRowDTO } from "../../../types/FinancialDTO";
import { formatCurrency, formatDate, monthLabel, statusColor } from "../../../utils/financialFunctions";
import { compareReviewRows, matchesReviewFilter, visibleSelection } from "../../../utils/financialSelection";
import { useAlert } from "../../contextProviders/AlertProvider";
import BillDetailModal from "../detail-modal/BillDetailModal";
import RemittanceModal from "../remittance-modal/RemittanceModal";
import "./FinancialReview.scss";

export const rowKey = (row: FinancialReviewRowDTO) => row.current ? `c${row.current.idConta}` : `p${row.previous?.idConta}`;

const billCell = (bill?: FinancialBillDTO) => bill
	? <Tooltip title={bill.statusText}>
		<TableCell align="right" className="financial-nowrap" sx={{ backgroundColor: statusColor(bill.status) }}>
			{formatCurrency(bill.value)}
		</TableCell>
	</Tooltip>
	: <TableCell align="center">—</TableCell>;

const FinancialReview: FC = () => {
	const [months, setMonths] = useState<string[]>([]);
	const [previous, setPrevious] = useState('');
	const [current, setCurrent] = useState('');
	const [rows, setRows] = useState<FinancialReviewRowDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [checkedFilter, setCheckedFilter] = useState('ALL');
	const [onlyDifferent, setOnlyDifferent] = useState(false);
	const [ascending, setAscending] = useState(true);
	const [sortKey, setSortKey] = useState('NAME');
	const [selected, setSelected] = useState<number[]>([]);
	const [paidConfirmation, setPaidConfirmation] = useState<FinancialBillDTO | null>(null);
	const [remittanceOpen, setRemittanceOpen] = useState(false);
	const [detailRow, setDetailRow] = useState<FinancialReviewRowDTO | null>(null);
	const { showError } = useAlert();
	const service = new FinancialService();

	useEffect(() => {
		const loadMonths = async () => {
			const response = await service.getMonths();
			const available = response?.data || [];
			setMonths(available);
			setCurrent(available[0] || '');
			setPrevious(available[1] || '');
			if (!available.length) {
				setLoading(false);
			}
		};
		loadMonths();
	}, []);

	const load = async () => {
		if (!previous || !current) {
			return;
		}
		setLoading(true);
		const response = await service.getReview(previous, current);
		if (!response) {
			showError('Não foi possível carregar a conferência. Recarregue a página para tentar novamente.');
		}
		setRows(response?.data || []);
		setSelected([]);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [previous, current]);

	const visible = useMemo(() => {
		const term = search.trim().toLowerCase();
		const digits = term.replace(/\D/g, '');
		return rows
			.filter(row => matchesReviewFilter(row, checkedFilter, onlyDifferent))
			.filter(row => !term || row.companyName?.toLowerCase().includes(term)
				|| (!!digits && (row.document || '').replace(/\D/g, '').includes(digits)))
			.sort((a, b) => compareReviewRows(a, b, sortKey, ascending));
	}, [rows, search, checkedFilter, onlyDifferent, ascending, sortKey]);

	const sortBy = (key: string) => {
		setAscending(sortKey === key ? !ascending : key === 'NAME');
		setSortKey(key);
	};

	const sortLabel = (key: string, label: string) => (
		<TableSortLabel active={sortKey === key} direction={sortKey === key && !ascending ? 'desc' : 'asc'} onClick={() => sortBy(key)}>
			{label}
		</TableSortLabel>
	);

	const selectable = visible.filter(row => row.current).map(row => row.current as FinancialBillDTO);
	const chosen = visibleSelection(selected, selectable.map(bill => bill.idConta));

	const toggle = (bill: FinancialBillDTO, confirmed = false) => {
		if (selected.includes(bill.idConta)) {
			setSelected(selected.filter(id => id !== bill.idConta));
			return;
		}
		if (bill.status === 'PAID' && !confirmed) {
			setPaidConfirmation(bill);
			return;
		}
		setSelected([...selected, bill.idConta]);
	};

	const toggleAll = () => {
		const unpaid = selectable.filter(bill => bill.status !== 'PAID').map(bill => bill.idConta);
		setSelected(chosen.length ? [] : unpaid);
	};

	const check = async (row: FinancialReviewRowDTO, checked: boolean) => {
		if (!row.current) {
			return;
		}
		const response = await service.setChecked(row.current.idConta, checked);
		if (!response) {
			showError('Não foi possível salvar a conferência. Tente novamente.');
			return;
		}
		setRows(rows.map(item => rowKey(item) === rowKey(row) ? { ...item, checked } : item));
	};

	const names = Object.fromEntries(rows.filter(row => row.current).map(row => [row.current?.idConta, row.companyName]));

	const companyLabel = (row: FinancialReviewRowDTO) => {
		const bill = row.current || row.previous;
		return bill && !bill.monthly ? `${row.companyName} (avulsa ${formatDate(bill.issueDate)})` : row.companyName;
	};

	return (
		<div className="financial-review">
			<div className="financial-filters">
				<FormControl size="small" sx={{ minWidth: 170 }}>
					<InputLabel id="review-previous">Mês anterior</InputLabel>
					<Select labelId="review-previous" label="Mês anterior" value={previous} onChange={(e) => setPrevious(e.target.value)}>
						{months.filter(month => month < current).map(month => <MenuItem key={month} value={month}>{monthLabel(month)}</MenuItem>)}
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 170 }}>
					<InputLabel id="review-current">Mês atual</InputLabel>
					<Select labelId="review-current" label="Mês atual" value={current} onChange={(e) => {
						setCurrent(e.target.value);
						if (previous >= e.target.value) {
							setPrevious(months.find(month => month < e.target.value) || '');
						}
					}}>
						{months.slice(0, -1).map(month => <MenuItem key={month} value={month}>{monthLabel(month)}</MenuItem>)}
					</Select>
				</FormControl>
				<TextField size="small" label="Buscar por nome ou CNPJ" value={search} onChange={(e) => setSearch(e.target.value)}
					sx={{ width: { xs: '100%', md: '280px' } }}/>
				<FormControl size="small" sx={{ minWidth: 150 }}>
					<InputLabel id="review-checked">Filtrar</InputLabel>
					<Select labelId="review-checked" label="Filtrar" value={checkedFilter} onChange={(e) => setCheckedFilter(e.target.value)}>
						<MenuItem value="ALL">Todas</MenuItem>
						<MenuItem value="CHECKED">Conferidas</MenuItem>
					</Select>
				</FormControl>
				<FormControlLabel control={<Switch checked={onlyDifferent} onChange={(e) => setOnlyDifferent(e.target.checked)}/>}
					label="Com diferença"/>
				<Button variant="contained" startIcon={<Description/>} disabled={!chosen.length} onClick={() => setRemittanceOpen(true)}
					className="financial-action">
					{`Gerar .REM${chosen.length ? ` (${chosen.length})` : ''}`}
				</Button>
			</div>
			{loading
				? <div className="financial-loading"><CircularProgress/></div>
				: <TableContainer className="financial-table">
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox">
									<Checkbox indeterminate={!!chosen.length && chosen.length < selectable.length}
										checked={!!selectable.length && chosen.length === selectable.length} onChange={toggleAll}/>
								</TableCell>
								<TableCell>
									{sortLabel('NAME', 'Empresa')}
								</TableCell>
								<TableCell>CNPJ</TableCell>
								<TableCell align="right">{sortLabel('PREVIOUS', previous ? monthLabel(previous) : '')}</TableCell>
								<TableCell align="right">{sortLabel('CURRENT', current ? monthLabel(current) : '')}</TableCell>
								<TableCell align="right">{sortLabel('DIFFERENCE', 'Diferença')}</TableCell>
								<TableCell align="center">Detalhes</TableCell>
								<TableCell align="center">Conferido</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{visible.map(row => (
								<TableRow key={rowKey(row)} hover>
									<TableCell padding="checkbox">
										{row.current &&
											<Checkbox checked={selected.includes(row.current.idConta)} onChange={() => toggle(row.current as FinancialBillDTO)}/>
										}
									</TableCell>
									<TableCell>
										{companyLabel(row)}
										{row.remittances?.map(sequence => (
											<Chip key={sequence} size="small" label={`Remessa nº ${sequence}`} sx={{ ml: 1 }}/>
										))}
									</TableCell>
									<TableCell className="financial-nowrap">{row.document}</TableCell>
									{billCell(row.previous)}
									{billCell(row.current)}
									<TableCell align="right" className="financial-nowrap"
										sx={{ backgroundColor: Math.abs(row.difference) >= 0.005 ? 'rgba(255, 235, 59, 0.28)' : undefined }}>
										{formatCurrency(row.difference)}
									</TableCell>
									<TableCell align="center">
										<Button size="small" onClick={() => setDetailRow(row)}>Ver detalhes</Button>
									</TableCell>
									<TableCell align="center">
										<Checkbox checked={row.checked} disabled={!row.current} onChange={(e) => check(row, e.target.checked)}/>
									</TableCell>
								</TableRow>
							))}
							{!visible.length &&
								<TableRow>
									<TableCell colSpan={8} align="center">Nenhuma fatura encontrada.</TableCell>
								</TableRow>
							}
						</TableBody>
					</Table>
				</TableContainer>
			}
			<Dialog open={!!paidConfirmation} onClose={() => setPaidConfirmation(null)}>
				<DialogTitle>Fatura já paga</DialogTitle>
				<DialogContent>Esta fatura já está paga. Incluir mesmo assim?</DialogContent>
				<DialogActions>
					<Button onClick={() => setPaidConfirmation(null)}>Não</Button>
					<Button variant="contained" onClick={() => {
						if (paidConfirmation) {
							toggle(paidConfirmation, true);
						}
						setPaidConfirmation(null);
					}}>Incluir</Button>
				</DialogActions>
			</Dialog>
			<BillDetailModal row={detailRow} onClose={() => setDetailRow(null)}/>
			<RemittanceModal open={remittanceOpen} idContas={chosen} names={names} onClose={() => setRemittanceOpen(false)}
				onGenerated={() => {
					setRemittanceOpen(false);
					load();
				}}/>
		</div>
	);
}

export default FinancialReview
